import { CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LIMIT_EXPLANATIONS, PLAN_LIMITS, SELECTABLE_PLAN_YEARS } from '@/lib/constants'
import { formatCurrency } from '@/lib/utils'
import PlanCardShell from '@/components/plan/PlanCardShell'

function LimitCard({ label, value, description }) {
  return (
    <div className="rounded-lg border p-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="mt-1 font-mono font-medium">{value}</div>
      {description && <p className="mt-2 text-xs text-muted-foreground">{description}</p>}
    </div>
  )
}

export default function PlanYearCard({ id, title, reference, planYear, onPlanYearChange, limits, disabled, variant }) {
  const hceLookbackYear = planYear - 1
  const hceLookbackThreshold = PLAN_LIMITS[hceLookbackYear]?.hceThreshold

  return (
    <PlanCardShell id={id} disabled={disabled} variant={variant} title={title} collapsible={false}>
      <CardContent className="space-y-4">
        {reference}
        <div className="max-w-xs">
          <Label>Plan year</Label>
          <Select value={String(planYear)} onValueChange={onPlanYearChange}>
            <SelectTrigger aria-label={`${typeof title === 'string' ? title : 'Plan year & limits'} plan year`} className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SELECTABLE_PLAN_YEARS.map((year) => <SelectItem key={year} value={String(year)}>{year}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {limits && (
          <details className="rounded-md border border-dashed p-2">
            <summary className="cursor-pointer text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{planYear} IRS limits</span>
              {': '}deferral {formatCurrency(limits.deferralLimit)}, §415(c) {formatCurrency(limits.planLimit415c)}, comp cap {formatCurrency(limits.compCap401a17)} (expand for all)
            </summary>
            <div className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-3">
              <LimitCard label="Deferral limit" value={formatCurrency(limits.deferralLimit)} description={LIMIT_EXPLANATIONS.deferralLimit} />
              <LimitCard label="Catch-up (50-59, 64+)" value={formatCurrency(limits.catchup50to59)} description={LIMIT_EXPLANATIONS.catchup50to59} />
              <LimitCard label="Super catch-up (60-63)" value={formatCurrency(limits.superCatchup60to63)} description={LIMIT_EXPLANATIONS.superCatchup60to63} />
              <LimitCard label="415(c) limit" value={formatCurrency(limits.planLimit415c)} description={LIMIT_EXPLANATIONS.planLimit415c} />
              <LimitCard label="Compensation cap" value={formatCurrency(limits.compCap401a17)} description={LIMIT_EXPLANATIONS.compCap401a17} />
              <LimitCard
                label={`HCE lookback threshold (${hceLookbackYear})`}
                value={formatCurrency(hceLookbackThreshold ?? limits.hceThreshold)}
                description={`For ${planYear} HCE classification, prior-year compensation in excess of this ${hceLookbackYear} threshold is used. Current compensation is an estimate when prior-year compensation is missing.`}
              />
              {limits.keyEmployeeThreshold != null && <LimitCard label="Key employee threshold" value={formatCurrency(limits.keyEmployeeThreshold)} description={LIMIT_EXPLANATIONS.keyEmployeeThreshold} />}
            </div>
          </details>
        )}
      </CardContent>
    </PlanCardShell>
  )
}
